import { useEffect } from 'react';
import { Alert, Form, Input, InputNumber, Modal, Switch, Typography } from 'antd';
import { nhomNghin } from '../../format';
import type { SystemConfig } from './api';
import { tokens } from '../../theme';

interface ConfigEditModalProps {
  config: SystemConfig | null;
  submitting: boolean;
  onSubmit: (value: number, reason: string) => void;
  onCancel: () => void;
}

interface FormValues {
  value: number | boolean;
  reason: string;
}

export function ConfigEditModal({ config, submitting, onSubmit, onCancel }: ConfigEditModalProps) {
  const [form] = Form.useForm<FormValues>();

  useEffect(() => {
    if (!config) return;
    // Lý do KHÔNG mang theo giá trị cũ: nó phải là lý do của lần đổi này, không phải
    // của lần trước. Để sẵn chữ cũ là mời người dùng bấm qua mà không đọc.
    form.setFieldsValue({
      value: config.type === 'Boolean' ? config.value !== 0 : config.value,
      reason: '',
    });
  }, [config, form]);

  const isBoolean = config?.type === 'Boolean';

  return (
    <Modal
      open={config !== null}
      title={config ? `Sửa ${config.key}` : ''}
      okText="Lưu thay đổi"
      cancelText="Hủy"
      confirmLoading={submitting}
      onCancel={onCancel}
      destroyOnHidden
      onOk={() => {
        form
          .validateFields()
          .then((values) => {
            onSubmit(
              typeof values.value === 'boolean' ? (values.value ? 1 : 0) : values.value,
              values.reason.trim(),
            );
          })
          // Ô không hợp lệ đã hiện lỗi ngay dưới nó — nuốt để không thành
          // unhandled rejection đổ ra console và hệ thống báo lỗi
          .catch(() => {});
      }}
    >
      {config && (
        <>
          <Typography.Paragraph style={{ color: tokens.color.ink2 }}>
            {config.description}
          </Typography.Paragraph>

          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={
              isBoolean
                ? `Mặc định: ${config.defaultValue !== 0 ? 'Bật' : 'Tắt'}`
                : `Mặc định ${config.defaultValue} ${config.unit} · cho phép từ ${config.minimum} tới ${config.maximum} ${config.unit}`
            }
          />

          <Form form={form} layout="vertical" requiredMark={false}>
            {/* Nhãn nằm TRÊN ô nhập, không dùng placeholder thay nhãn */}
            <Form.Item
              name="value"
              label={isBoolean ? 'Trạng thái' : `Giá trị (${config.unit})`}
              valuePropName={isBoolean ? 'checked' : 'value'}
              rules={
                isBoolean
                  ? undefined
                  : [
                      { required: true, message: 'Chưa nhập giá trị.' },
                      {
                        type: 'number',
                        min: config.minimum,
                        max: config.maximum,
                        message: `Phải nằm trong khoảng ${config.minimum} tới ${config.maximum} ${config.unit}.`,
                      },
                    ]
              }
            >
              {isBoolean ? (
                <Switch checkedChildren="Bật" unCheckedChildren="Tắt" />
              ) : (
                <InputNumber
                  style={{ width: '100%' }}
                  min={config.minimum}
                  max={config.maximum}
                  step={config.type === 'Integer' ? 1 : 0.1}
                  precision={config.type === 'Integer' ? 0 : undefined}
                formatter={nhomNghin.formatter}
                  parser={nhomNghin.parser}
                />
              )}
            </Form.Item>

            <Form.Item
              name="reason"
              label="Lý do thay đổi"
              tooltip="Sáu tháng sau nhìn lại một con số lạ mà không có lý do thì không ai dám sửa."
              rules={[
                { required: true, message: 'Chưa nhập lý do thay đổi.' },
                { max: 500, message: 'Lý do quá dài.' },
              ]}
            >
              <Input.TextArea rows={2} placeholder="Mạng chậm giờ cao điểm, khách không kịp nhận mã" />
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
}
